import React from 'react'
import styled from 'styled-components'
import dayjs from 'dayjs'

import { getSrcSetStr } from 'helpers/image'
import { Fonts, Icon } from 'components/base-ui'
import { useTranslation } from 'react-i18next'

import { AdjustmentInvalidReasonEnumType, AdjustmentSourceEnumType } from 'enums'
import {
  ExternalCouponRewardTypeEnum,
  AdjustmentTargetEntityTypeEnum,
  OrderAndFpMemberCouponsQuery,
} from 'graphQL/dinein/generate/operations'
import {
  HandleApplyPromoCode,
  HandleRemoveCartAdjustment,
  HandleToShowPromotionDetails,
  CustomerAdjustmentInvalidDataType,
} from 'types'
import { CouponType } from './PromotionDetails'
import CouponOwner from './base-ui/CouponOwner'

export interface DineInCoupon extends CouponType {
  membership?: OrderAndFpMemberCouponsQuery['orderAndFPMemberCoupons']['coupons'][number]['membership']
}

interface CouponCardCheckoutProps {
  data: DineInCoupon
  isActive: boolean
  isDisabled?: boolean
  externalCouponID?: number
  promotionID?: number
  hatoHeartMemberCouponID?: number
  handleApplyPromoCode: HandleApplyPromoCode
  handleRemoveCartAdjustment: HandleRemoveCartAdjustment
  isExternalMemberCoupon: boolean
  invalidData?: CustomerAdjustmentInvalidDataType
  invalidReason?: AdjustmentInvalidReasonEnumType
  handleToShowPromotionDetails: HandleToShowPromotionDetails
  targetEntity?: keyof typeof AdjustmentTargetEntityTypeEnum
  haveMemberCondition: boolean
  isAutoPromotion?: boolean
  isLoading: boolean
}

const PromotionCouponCard = ({
  data,
  isActive,
  isDisabled = false,
  handleToShowPromotionDetails,
  externalCouponID,
  promotionID,
  hatoHeartMemberCouponID,
  handleRemoveCartAdjustment,
  handleApplyPromoCode,
  invalidData,
  invalidReason,
  targetEntity,
  haveMemberCondition,
  isAutoPromotion = false,
  isLoading = false,
}: CouponCardCheckoutProps) => {
  const { t } = useTranslation()

  const isRedWarning = invalidReason ? true : false
  let isInvalidConditions = false
  switch (true) {
    case invalidReason === AdjustmentInvalidReasonEnumType.Expired:
      break
    case invalidReason === AdjustmentInvalidReasonEnumType.CouponHasBeenUsed ||
      invalidReason === AdjustmentInvalidReasonEnumType.CodeHasBeenUsed:
      break
    case invalidReason === AdjustmentInvalidReasonEnumType.InvalidConditions:
      isInvalidConditions = true
      break
    default:
      break
  }

  let iconImage = ''
  switch (targetEntity) {
    case AdjustmentTargetEntityTypeEnum.ShippingFee:
      iconImage = haveMemberCondition
        ? '/assets/generic/promotions/icon-promotion-member-shippingfee.png'
        : '/assets/generic/promotions/icon-promotion-general-shippingfee.png'
      break
    default:
      iconImage = haveMemberCondition
        ? '/assets/generic/promotions/icon-promotion-member-discount.png'
        : '/assets/generic/promotions/icon-promotion-general-discount.png'
  }

  if (data.rewardType === ExternalCouponRewardTypeEnum.Cash) {
    iconImage = '/assets/generic/promotions/icon-promotion-cash-voucher.png'
  }

  if (data.thumbnailImageURL) {
    iconImage = data.thumbnailImageURL
  }

  return (
    <PromotionCardWrapper id={`wrapper-${data.serialNumber}`}>
      {isActive && !isAutoPromotion && (
        <BtnRemove
          className="btn-remove-promotion"
          onClick={(e) => {
            e.stopPropagation()
            removeCouponFromCart()
          }}
        >
          <Icon name="plus-2" />
        </BtnRemove>
      )}
      <PromotionCard
        id={data.serialNumber}
        onClick={() =>
          handleToShowPromotionDetails(
            data,
            targetEntity,
            haveMemberCondition,
            isAutoPromotion,
            invalidReason,
            invalidData
          )
        }
      >
        <TopCardBg isActive={isActive} isWarning={isRedWarning} />
        <BottomCardBg isActive={isActive} isWarning={isRedWarning} />
        <LeftBorderBox isActive={isActive} isWarning={isRedWarning} />
        <RightBorderBox isActive={isActive} isWarning={isRedWarning} />
        <ImageIconCouponBox>
          {data.thumbnailImageURL ? (
            <ImageThumbnailHHCoupon style={{ backgroundImage: `url('${data.thumbnailImageURL}')` }} />
          ) : (
            <ImageIconCoupon
              alt="promotion-icon-coupon"
              src={`${process.env.PUBLIC_URL}${iconImage}`}
              srcSet={getSrcSetStr({
                img: `${process.env.PUBLIC_URL}${iconImage}`,
                hires2x: true,
                hires3x: true,
              })}
            />
          )}
        </ImageIconCouponBox>
        <DetailsBox isDisabled={isDisabled}>
          <PromotionTitle isActive={isActive && !isRedWarning}>{data.name}</PromotionTitle>
          <PromotionExpired>
            {isActive && !isRedWarning && <LabelActive>{t('promotionUsedSwitch')}</LabelActive>}
            {data.isAvailable || isActive
              ? (() => {
                  if (data.membership) return <CouponOwner {...data.membership.user} />
                  switch (invalidReason) {
                    case AdjustmentInvalidReasonEnumType.CodeHasBeenUsed:
                    case AdjustmentInvalidReasonEnumType.CouponHasBeenUsed:
                      return <LabelInvalid>{t('invalidConditionUsed')}</LabelInvalid>
                    case AdjustmentInvalidReasonEnumType.InvalidConditions:
                      return <LabelInvalid>{t('invalidCondition')}</LabelInvalid>
                    default:
                      return (
                        data.endTime &&
                        !isActive && (
                          <PromotionDateSection>
                            {t('expiredOn')} {dayjs(data.endTime).format('D/M/YY')}
                          </PromotionDateSection>
                        )
                      )
                  }
                })()
              : t('notAvailablePromotionInThisChannel')}

            {(isInvalidConditions || isActive) && (
              <LinkSeeCondition>
                <Fonts.LinkSmall>{t('conditions')}</Fonts.LinkSmall>
                <IconArrowRight>
                  <Icon name="carret-left" />
                </IconArrowRight>
              </LinkSeeCondition>
            )}

            {!isActive && !isDisabled && (
              <BtnApplyPromotion
                onClick={(e) => {
                  e.stopPropagation()
                  if (isLoading) {
                    return
                  }
                  applyCouponToCart()
                }}
              >
                <PreloadImage src={`${process.env.PUBLIC_URL}/assets/loading/loading-white.svg`} />
                {isLoading ? (
                  <LoadingImg
                    src={`${process.env.PUBLIC_URL}/assets/loading/loading-white.svg`}
                    width="20px"
                    height="20px"
                    alt=""
                  />
                ) : (
                  <Fonts.SmallTitleBold>{t('use')}</Fonts.SmallTitleBold>
                )}
              </BtnApplyPromotion>
            )}
          </PromotionExpired>
        </DetailsBox>
      </PromotionCard>
    </PromotionCardWrapper>
  )

  function applyCouponToCart() {
    if (!data) return

    const { source, serialNumber, id } = data
    const isPromotion = source === AdjustmentSourceEnumType.Promotion && serialNumber
    const isExternalCoupon =
      source === AdjustmentSourceEnumType.ExternalCoupon || source === AdjustmentSourceEnumType.ExternalMemberCoupon
    const isMemberCoupon = source === AdjustmentSourceEnumType.MemberCoupon

    if (isPromotion || isExternalCoupon) {
      handleApplyPromoCode({
        id,
        promoCode: serialNumber,
      })
    } else if (isMemberCoupon) {
      handleApplyPromoCode({
        id,
        hatoHeartMemberCouponID: id,
      })
    } else {
      handleApplyPromoCode({
        id,
      })
    }
  }

  function removeCouponFromCart() {
    if (
      (externalCouponID === undefined || externalCouponID === null) &&
      (promotionID === undefined || promotionID === null) &&
      (hatoHeartMemberCouponID === undefined || hatoHeartMemberCouponID === null)
    ) {
      return false
    }

    handleRemoveCartAdjustment({
      externalCouponID,
      promotionID: hatoHeartMemberCouponID || externalCouponID ? undefined : promotionID,
      hatoHeartMemberCouponID,
    })
  }
}

export default PromotionCouponCard

const PromotionCardWrapper = styled.div`
  position: relative;
`

const PromotionCard = styled.div`
  position: inherit;
  border-radius: ${({ theme }) => theme.radius.md};
  overflow: hidden;
  width: 100%;
  min-height: 100px;
  filter: drop-shadow(2px 2px 5px rgba(0, 0, 0, 0.05));
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.S} 0;
  padding-right: 15px;
  margin-bottom: ${({ theme }) => theme.spacing.XL};
  z-index: 98;
`

const TopCardBg = styled.div<{ isActive: boolean; isWarning: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 50%;
  right: 0;
  overflow: hidden;
  padding: 0 ${({ theme }) => theme.spacing.S};

  &::before {
    content: '';
    display: block;
    width: 12px;
    height: 12px;
    border-radius: ${({ theme }) => theme.radius.fullyRounded};
    box-shadow: 0 0 0 500px ${({ theme }) => theme.colors.element.white};
    position: absolute;
    top: 0;
    left: 94px;
    margin-top: -7px;
    ${({ isActive, theme }) =>
      isActive && `box-shadow: 0 0 0 500px ${theme.colors.green100}, inset 0 0 0 2px ${theme.colors.success};`}
    ${({ isWarning, theme }) =>
      isWarning &&
      `box-shadow: 0 0 0 500px ${theme.colors.element.white}, inset 0 0 0 2px ${theme.colors.warningDark};`}
  }
`

const BottomCardBg = styled.div<{ isActive: boolean; isWarning: boolean }>`
  position: absolute;
  top: 50%;
  left: 0;
  bottom: 0;
  right: 0;
  overflow: hidden;

  &::before {
    content: '';
    display: block;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    box-shadow: 0 0 0 500px ${({ theme }) => theme.colors.element.white};
    position: absolute;
    bottom: 0;
    left: 94px;
    margin-bottom: -7px;
    ${({ isActive, theme }) =>
      isActive && `box-shadow: 0 0 0 500px ${theme.colors.green100}, inset 0 0 0 2px ${theme.colors.success};`}
    ${({ isWarning, theme }) =>
      isWarning && `box-shadow: 0 0 0 500px white, inset 0 0 0 2px ${theme.colors.warningDark};`}
  }
`

const LeftBorderBox = styled.div<{ isActive: boolean; isWarning: boolean }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 95px;
  border-top-left-radius: 10px;
  border-bottom-left-radius: 10px;

  ${({ isActive, theme }) =>
    isActive &&
    `
    border: 2px solid ${theme.colors.success};
  `}

  ${({ isWarning, theme }) =>
    isWarning &&
    `
    border: 2px solid ${theme.colors.warningDark};
  `}

  border-right: 0;
`

const RightBorderBox = styled.div<{ isActive: boolean; isWarning: boolean }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 105px;
  right: 0;
  border-top-right-radius: 10px;
  border-bottom-right-radius: 10px;

  ${({ isActive, theme }) =>
    isActive &&
    `
    border: 2px solid ${theme.colors.success};
  `}

  ${({ isWarning, theme }) =>
    isWarning &&
    `
    border: 2px solid ${theme.colors.warningDark};
  `}

  border-left: 0;
`

const ImageIconCouponBox = styled.div`
  position: relative;
  width: 100px;
  height: 80px;
  border-right: 1px dashed #ebebeb;
  display: flex;
  align-items: center;
  justify-content: center;
`

const ImageIconCoupon = styled.img`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`

const ImageThumbnailHHCoupon = styled.div`
  width: 70px;
  height: 70px;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-size: cover;
  background-position: center;
`

const DetailsBox = styled.div<{ isDisabled: boolean }>`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 80px;
  position: relative;
  padding: ${({ theme }) => theme.spacing['2XS']} 0;
  padding-left: 18px;
  color: ${({ theme }) => theme.colors.base};

  ${({ isDisabled, theme }) =>
    isDisabled
      ? `
    color: ${theme.colors.baseGrey};
  `
      : ''}
`

const PromotionTitle = styled(Fonts.ActionBaseBold)<{ isActive: boolean }>`
  margin-bottom: ${({ theme }) => theme.spacing['2XS']};
  ${({ isActive, theme }) =>
    isActive &&
    `
    color: ${theme.colors.lime900};
  `};
`

const PromotionExpired = styled(Fonts.SmallTitleRegular)`
  display: flex;
  margin-top: auto;
  align-items: center;
  white-space: nowrap;
  justify-content: space-between;
`

const LabelInvalid = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding: 0 ${({ theme }) => theme.spacing.XS};
  padding-top: 1px;
  border-radius: ${({ theme }) => theme.radius.xl};
  color: ${({ theme }) => theme.colors.warningDark};
  background-color: ${({ theme }) => theme.colors.gold200};
  font-size: ${({ theme }) => theme.fontSize.sm};
`

const LabelActive = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding: 0 ${({ theme }) => theme.spacing.XS};
  padding-top: 1px;
  border-radius: ${({ theme }) => theme.radius.xl};
  color: ${({ theme }) => theme.colors.element.white};
  background-color: ${({ theme }) => theme.colors.success};
  font-size: ${({ theme }) => theme.fontSize.sm};
`

const PromotionDateSection = styled.span`
  margin-right: 8px;
`

const BtnRemove = styled.div`
  width: 33px;
  height: 33px;
  background-color: ${({ theme }) => theme.colors.element.white};
  border-radius: 50%;
  box-shadow: 0.5px 0.5px 10px rgba(0, 0, 0, 0.25);
  position: absolute;
  top: -10px;
  right: -10px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99;
  i {
    transform: rotate(45deg);
    font-size: ${({ theme }) => theme.fontSize.xl};
    color: ${({ theme }) => theme.colors.infoDark};
  }
`

const PreloadImage = styled.img`
  display: none;
`

const LoadingImg = styled.img`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`

const LinkSeeCondition = styled.div`
  display: flex;
  color: ${({ theme }) => theme.colors.infoDark};
  pointer-events: none;
`

const IconArrowRight = styled.div`
  margin-left: 5px;

  i {
    position: relative;
    top: 1px;
    display: block;
    font-size: ${({ theme }) => theme.fontSize.lg};
    transform: rotate(180deg);
    color: ${({ theme }) => theme.colors.infoDark};
  }
`

const BtnApplyPromotion = styled.div`
  position: relative;
  margin-left: auto;
  height: 25px;
  min-width: 42px;
  border-radius: ${({ theme }) => theme.radius.sm};
  padding: 0 ${({ theme }) => theme.spacing.XS};
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.element.white};
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 16px;
  }
`
